import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import Actions_Wind from "~/Actions_Wind";
import Actions_Wind_V2 from "~/Actions_Wind_V2";
import permission from "~/skills/permission";
import {
    Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField,
    Dialog, DialogTitle, DialogContent, DialogActions, Typography, TablePagination, Grid, Link, makeStyles
} from "@material-ui/core";

import GroupCreateDialog from "./dialogs/GroupCreateDialog";
import GroupEditDialog from "./dialogs/GroupEditDialog";
import GroupViewDialog from "./dialogs/GroupViewDialog";

const useStyles = makeStyles((theme) => ({
    searchButton: {
        backgroundColor: "#5597b4",
        color: "#fff",
        borderRadius: "6px",
        "&:hover": {
            backgroundColor: "#3E8CCB",
        },
    },
    addButton: {
        backgroundColor: "#fff",
        color: "#5597b4",
        border: "1px solid #5597b4",
        borderRadius: "6px",
        "&:hover": {
            backgroundColor: "#E6F2FB",
        },
    },
    editButton: {
        backgroundColor: "#fff",
        color: "#5597b4",
        border: "1px solid #5597b4",
        borderRadius: "6px",
        "&:hover": {
            backgroundColor: "#E6F2FB",
        },
    },
    viewButton: {
        backgroundColor: "#5597b4",
        color: "#fff",
        borderRadius: "4px",
        "&:hover": {
            backgroundColor: "#3E8CCB",
        },
    },
    deleteButton: {
        backgroundColor: "#999",
        color: "#fff",
        borderRadius: "4px",
        "&:hover": {
            backgroundColor: "#777",
        },
    },
    headerRow: {
        backgroundColor: "#f7f7f7",
    },
    oddRow: {
        backgroundColor: "#E6F7FB",
    },
}));

function Page_GroupManagement(props) {
    const { getWindcontractGroups, getAWindcontractGroup, createAWindcontractGroup, updateAWindcontractGroup, deleteAWindcontractGroup } = props;
    const classes = useStyles();

    const [search, setSearch] = useState("");
    const [openCreate, setOpenCreate] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [selectedId, setSelectedId] = useState(null);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [rows, setRows] = useState([]);
    const [totalCount, setTotalCount] = useState(0);

    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const addBtnRef = useRef(null);

    // fetch list
    const handleSearch = async (pageIndex = page, pageSize = rowsPerPage) => {
        try {
            const body = {
                keyword: search || "",
                page_index: pageIndex,
                page_size: pageSize,
            };

            const result = await getWindcontractGroups(body);
            const dataArr = result?.data || result?.data?.data || result?.data?.data?.data || result;
            let groups = [];
            let total = 0;

            if (Array.isArray(result?.data)) {
                groups = result.data;
                total = result?.pagination_info?.total || groups.length;
            } else if (Array.isArray(result?.data?.data)) {
                groups = result.data.data;
                total = result.data.pagination_info?.total || groups.length;
            } else if (Array.isArray(dataArr)) {
                groups = dataArr;
            }

            const mapped = (groups || []).map((g, idx) => ({
                id: g.id,
                serial: pageIndex * pageSize + idx + 1,
                group_name: g.group_name || g.windcontract_group_name || "-",
                note: g.note || "-",
            }));
            setRows(mapped);
            setTotalCount(total || mapped.length);
        } catch (err) {
            console.error("get windcontract groups failed:", err);
        }
    };

    useEffect(() => {
        handleSearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage]);

    const handleAdd = () => {
        setOpenCreate(true);
    };

    const handleCreateSave = async (payload) => {
        // payload: { windcontract_group_name, note }
        try {
            await createAWindcontractGroup(payload);
            await handleSearch(0, rowsPerPage);
        } catch (e) {
            console.error("create group failed", e);
            alert("建立失敗");
        } finally {
            setOpenCreate(false);
            addBtnRef.current?.focus();
        }
    };

    const handleView = (id) => {
        setSelectedId(id);
        setOpenView(true);
    };

    const handleEdit = (id) => {
        setSelectedId(id);
        setOpenEdit(true);
    };

    const handleEditSave = async (payload) => {
        // payload expected: { windcontract_group_id, windcontract_group_name, note }
        try {
            await updateAWindcontractGroup(payload);
            await handleSearch(page, rowsPerPage);
        } catch (e) {
            console.error("update group failed", e);
            alert("更新失敗");
        } finally {
            setOpenEdit(false);
            setSelectedId(null);
        }
    };

    const handleDelete = (id) => {
        setSelectedId(id);
        setDeleteConfirmOpen(true);
    };

    const confirmDelete = async () => {
        try {
            const success = await deleteAWindcontractGroup(selectedId);
            if (success) {
                await handleSearch(page, rowsPerPage);
            } else {
                alert("刪除失敗");
            }
        } catch (err) {
            console.error("刪除失敗:", err);
            alert("刪除失敗");
        } finally {
            setDeleteConfirmOpen(false);
            setSelectedId(null);
        }
    };

    // pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };
    const handleChangeRowsPerPage = (event) => {
        const rp = parseInt(event.target.value, 10);
        setRowsPerPage(rp);
        setPage(0);
    };

    return (
        <Box p={3} display="flex" flexDirection="column" style={{ overflowY: "auto" }}>
            <Typography variant="h6" gutterBottom style={{ fontWeight: "bold", textAlign: "left", marginRight: "auto" }}>
                群組管理
            </Typography>

            {/* 搜尋 */}
            <Grid container spacing={2} alignItems="center" style={{ marginBottom: 16 }}>
                <Grid item>
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="請輸入關鍵字（群組名稱或備註）"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </Grid>
                <Grid item>
                    <Button
                        variant="contained"
                        className={classes.searchButton}
                        onClick={() => {
                            setPage(0);
                            handleSearch(0, rowsPerPage);
                        }}
                    >
                        查詢
                    </Button>
                </Grid>
            </Grid>

            {/* 新增 */}
            <Box mb={2} display="flex" justifyContent="flex-start">
                <Button ref={addBtnRef} className={classes.addButton} variant="contained" onClick={handleAdd}>
                    + 新增群組
                </Button>
            </Box>

            {/* 表格 */}
            <TableContainer component={Paper} style={{ maxHeight: "60vh", overflowY: "auto" }}>
                <Table>
                    <TableHead>
                        <TableRow className={classes.headerRow}>
                            <TableCell>序號</TableCell>
                            <TableCell>群組名稱</TableCell>
                            <TableCell>備註</TableCell>
                            <TableCell style={{ textAlign: "center" }}>功能</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, idx) => (
                            <TableRow key={row.id} className={idx % 2 === 0 ? classes.oddRow : ""}>
                                <TableCell>{row.serial}</TableCell>
                                <TableCell>{row.group_name}</TableCell>
                                <TableCell>{row.note}</TableCell>
                                <TableCell>
                                    <Box display="flex" gridGap={8} justifyContent="center">
                                        <Button size="small" className={classes.viewButton} onClick={() => handleView(row.id)}>
                                            檢視
                                        </Button>
                                        <Button size="small" className={classes.editButton} onClick={() => handleEdit(row.id)}>
                                            編輯
                                        </Button>
                                        <Button size="small" className={classes.deleteButton} onClick={() => handleDelete(row.id)}>
                                            刪除
                                        </Button>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                        {rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4}>
                                    <Typography align="center" color="textSecondary">沒有資料</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>

                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onChangePage={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                    labelRowsPerPage="每頁筆數"
                    labelDisplayedRows={({ from, to, count }) => `第 ${from}-${to} 筆，共 ${count} 筆`}
                />
            </TableContainer>

            {/* 刪除確認 Dialog */}
            {deleteConfirmOpen && (
                <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
                    <DialogTitle>確認刪除</DialogTitle>
                    <DialogContent>
                        <Typography>確定要刪除此群組？刪除後無法復原。</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteConfirmOpen(false)}>取消</Button>
                        <Button onClick={confirmDelete} style={{ backgroundColor: "#999", color: "#fff" }} variant="contained">確認刪除</Button>
                    </DialogActions>
                </Dialog>
            )}

            {/* Create Dialog */}
            {openCreate && (
                <GroupCreateDialog
                    open={openCreate}
                    onClose={() => setOpenCreate(false)}
                    onSave={handleCreateSave}
                />
            )}

            {/* View Dialog */}
            {openView && selectedId && (
                <GroupViewDialog
                    open={openView}
                    onClose={() => { setOpenView(false); setSelectedId(null); }}
                    groupId={selectedId}
                    getGroup={getAWindcontractGroup}
                />
            )}

            {/* Edit Dialog */}
            {openEdit && selectedId && (
                <GroupEditDialog
                    open={openEdit}
                    onClose={() => { setOpenEdit(false); setSelectedId(null); }}
                    groupId={selectedId}
                    getGroup={getAWindcontractGroup}
                    onSave={handleEditSave}
                />
            )}
        </Box>
    );
}

const mapStateToProps = (state) => ({
    sign: state.data?.AtyFunction,
    ...permission.returnPermission(state),
});

const mapDispatchToProps = (dispatch) => ({
    getWindcontractGroups: (body) => {
        const fn = Actions_Wind.getWindcontractGroups || Actions_Wind_V2.getWindcontractGroups;
        return fn(body);
    },
    getAWindcontractGroup: (id) => {
        const fn = Actions_Wind.getAWindcontractGroup || Actions_Wind_V2.getAWindcontractGroup;
        return fn(id);
    },
    createAWindcontractGroup: (payload) => {
        const fn = Actions_Wind.createAWindcontractGroup || Actions_Wind_V2.createAWindcontractGroup;
        return fn(payload);
    },
    updateAWindcontractGroup: (payload) => {
        const fn = Actions_Wind.updateAWindcontractGroup || Actions_Wind_V2.updateAWindcontractGroup;
        return fn(payload);
    },
    deleteAWindcontractGroup: (id) => {
        const fn = Actions_Wind.deleteAWindcontractGroup || Actions_Wind_V2.deleteAWindcontractGroup;
        return fn(id);
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(Page_GroupManagement);
